/**
 * QUERY INTERPRETER WITH CONTEXT - PHASE 3
 * Enhanced Natural Language Understanding with Conversation Memory
 * 
 * Interprets user queries with context from previous queries and results
 * Handles follow-up questions, prioritization, and progressive filtering
 */

import { QueryContext } from '@/types/conversation';
import { interpretQueryWithClaude, ClaudeQueryRequest } from './claudeApiService';

// Types moved here since queryInterpreter.ts was deleted
export interface QueryInterpretation {
  dataType: 'invoices';
  filters: {
    status?: string;
    customerName?: string;
    minAmount?: number;
    maxAmount?: number;
    minDaysOverdue?: number;
    maxDaysOverdue?: number;
  };
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  userIntent: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface QueryError {
  error: boolean | string;
  message: string;
  suggestion?: string;
}

export interface ContextualQueryInterpretation extends QueryInterpretation {
  isFollowUp: boolean;
  followUpType?: 'prioritization' | 'filtering' | 'calculation' | 'expansion' | 'reset';
  contextUsed?: string;
}

/**
 * Interpret query with conversation context
 */
export async function interpretQueryWithContext(
  query: string,
  context?: QueryContext
): Promise<ContextualQueryInterpretation | QueryError> {
  console.log('═══════════════════════════════════════════════════════');
  console.log('[interpretQueryWithContext] 🎯 STARTING INTERPRETATION');
  console.log('[interpretQueryWithContext] Query:', query);
  console.log('[interpretQueryWithContext] Context provided:', !!context);
  if (context) {
    console.log('[interpretQueryWithContext] Context details:', {
      currentResultsCount: context.currentResults?.length || 0,
      previousResultsCount: context.previousResults?.length || 0,
      previousQuery: context.previousQuery,
      resultCount: context.resultCount,
      totalAmount: context.totalAmount,
    });
  }
  
  try {
    // Build request for Claude
    const claudeRequest: ClaudeQueryRequest = {
      query,
      context: context ? {
        previousQuery: context.previousQuery,
        currentResults: context.currentResults?.map(inv => ({
          invoiceNumber: inv.invoiceNumber,
          customerName: inv.customerName,
          amount: inv.amount,
          daysOverdue: inv.daysOverdue,
        })),
        previousResults: context.previousResults,
        resultCount: context.resultCount,
        totalAmount: context.totalAmount,
      } : undefined,
    };

    // Call Claude API
    const claudeResponse = await interpretQueryWithClaude(claudeRequest);
    console.log('[interpretQueryWithContext] ✅ Claude interpretation:', claudeResponse);

    // Convert Claude response to our format
    return {
      dataType: claudeResponse.dataType,
      filters: claudeResponse.filters || {},
      sortBy: claudeResponse.sortBy || 'amount',
      sortOrder: claudeResponse.sortOrder || 'desc',
      userIntent: claudeResponse.userIntent,
      confidence: claudeResponse.confidence,
      isFollowUp: claudeResponse.isFollowUp || false,
      followUpType: claudeResponse.followUpType,
    };
  } catch (error) {
    console.error('[interpretQueryWithContext] ❓ Error:', error);
    return {
      error: 'Failed to interpret query',
      message: 'Sorry, I couldn\'t understand that query. Please try again.',
    };
  }
}





/**
 * Generate suggested follow-up questions based on current results
 */
export function generateSuggestedFollowUps(
  currentResults: any[],
  context?: QueryContext
): string[] {
  if (!currentResults || currentResults.length === 0) {
    return [];
  }

  const suggestions: string[] = [];

  // If multiple results, suggest prioritization
  if (currentResults.length > 1) {
    suggestions.push('Which should I chase first?');
  }

  // If multiple customers, suggest filtering
  const uniqueCustomers = new Set(currentResults.map(r => r.customerName));
  if (uniqueCustomers.size > 1) {
    const firstCustomer = Array.from(uniqueCustomers)[0];
    suggestions.push(`Just show me ${firstCustomer}`);
  }

  // Suggest amount filtering if there's a range
  const amounts = currentResults.map(r => r.amount);
  const maxAmount = Math.max(...amounts);
  const minAmount = Math.min(...amounts);
  if (maxAmount > minAmount * 2) {
    suggestions.push('Remove the small ones');
  }

  // Suggest total calculation
  if (currentResults.length > 1) {
    suggestions.push("What's the total?");
  }

  // Suggest comparative queries
  if (currentResults.length > 1) {
    suggestions.push('Which is older?');
  }

  return suggestions.slice(0, 3); // Return max 3 suggestions
}
